/**
 * Notification Context - Global state management for notifications
 */

import React, {
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import * as notificationService from "../services/notificationService.js";
import { NotificationContext } from "./notificationContextRef.js";
import {
  NOTIFICATION_POLL_INTERVAL_MS,
  attachNotificationSoundUnlock,
  playNotificationSound,
} from "../utils/notificationSound.js";

// Action types
const ACTIONS = {
  SET_NOTIFICATIONS: "SET_NOTIFICATIONS",
  SET_STATS: "SET_STATS",
  ADD_NOTIFICATION: "ADD_NOTIFICATION",
  MARK_AS_READ: "MARK_AS_READ",
  MARK_ALL_AS_READ: "MARK_ALL_AS_READ",
  DELETE_NOTIFICATION: "DELETE_NOTIFICATION",
  DELETE_ALL_NOTIFICATIONS: "DELETE_ALL_NOTIFICATIONS",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
};

// Initial state
const initialState = {
  notifications: [],
  stats: {
    total: 0,
    unread: 0,
    read: 0,
    actionRequired: 0,
    byCategory: {},
  },
  loading: false,
  error: null,
};

// Reducer function
const notificationReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload,
      };

    case ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        stats: {
          ...state.stats,
          total: state.stats.total + 1,
          unread: state.stats.unread + 1,
        },
      };

    case ACTIONS.MARK_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          notification.id === action.payload
            ? {
                ...notification,
                isRead: true,
                readAt: new Date().toISOString(),
              }
            : notification
        ),
        stats: {
          ...state.stats,
          unread: Math.max(0, state.stats.unread - 1),
          read: state.stats.read + 1,
        },
      };

    case ACTIONS.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map((notification) => ({
          ...notification,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
        stats: {
          ...state.stats,
          unread: 0,
          read: state.stats.total,
          actionRequired: 0,
        },
      };

    case ACTIONS.DELETE_NOTIFICATION: {
      const deletedNotification = state.notifications.find(
        (n) => n.id === action.payload
      );
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload
        ),
        stats: {
          ...state.stats,
          total: state.stats.total - 1,
          unread: deletedNotification?.isRead
            ? state.stats.unread
            : Math.max(0, state.stats.unread - 1),
          read: deletedNotification?.isRead
            ? Math.max(0, state.stats.read - 1)
            : state.stats.read,
        },
      };
    }

    case ACTIONS.DELETE_ALL_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        stats: {
          total: 0,
          unread: 0,
          read: 0,
          actionRequired: 0,
          byCategory: {},
        },
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    default:
      return state;
  }
};

// Provider component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { isAuthenticated, isInitialized } = useAuth();
  const previousUnreadRef = useRef(null);
  const pollInFlightRef = useRef(false);

  const loadNotifications = useCallback(async (filters = {}, options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    }
    try {
      const result = await notificationService.getNotifications(filters);
      if (result && result.success) {
        dispatch({
          type: ACTIONS.SET_NOTIFICATIONS,
          payload: result.notifications || [],
        });
      } else if (!silent) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: result?.error || "Failed to load notifications",
        });
      }
    } catch (error) {
      if (!silent) {
        dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      }
    }
  }, []);

  const loadStats = useCallback(async (options = {}) => {
    const { detectNew = false } = options;
    try {
      const result = await notificationService.getNotificationStats();
      if (result && result.success) {
        const nextStats = result.stats || {};
        const nextUnread = nextStats.unread || 0;
        const prevUnread = previousUnreadRef.current;

        if (detectNew && prevUnread !== null && nextUnread > prevUnread) {
          playNotificationSound();
        }

        previousUnreadRef.current = nextUnread;
        dispatch({ type: ACTIONS.SET_STATS, payload: nextStats });
      } else {
        console.error(
          "Failed to load notification stats:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to load notification stats:", error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      loadNotifications({ limit: 20, offset: 0 });
      loadStats({ detectNew: false });
    } else {
      previousUnreadRef.current = null;
    }
  }, [isAuthenticated, isInitialized, loadNotifications, loadStats]);

  useEffect(() => {
    if (!isAuthenticated || !isInitialized) return undefined;
    return attachNotificationSoundUnlock();
  }, [isAuthenticated, isInitialized]);

  useEffect(() => {
    if (!isAuthenticated || !isInitialized) return undefined;

    const tick = async () => {
      if (pollInFlightRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;

      pollInFlightRef.current = true;
      try {
        await loadStats({ detectNew: true });
        await loadNotifications({ limit: 20, offset: 0 }, { silent: true });
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const id = window.setInterval(tick, NOTIFICATION_POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) {
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, isInitialized, loadNotifications, loadStats]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const result =
        await notificationService.markNotificationAsRead(notificationId);
      if (result && result.success) {
        dispatch({ type: ACTIONS.MARK_AS_READ, payload: notificationId });
        previousUnreadRef.current = Math.max(
          0,
          (previousUnreadRef.current ?? 1) - 1
        );
      } else {
        console.error(
          "Failed to mark notification as read:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const result = await notificationService.markAllNotificationsAsRead();
      if (result && result.success) {
        dispatch({ type: ACTIONS.MARK_ALL_AS_READ });
        previousUnreadRef.current = 0;
      } else {
        console.error(
          "Failed to mark all notifications as read:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const result =
        await notificationService.deleteNotification(notificationId);
      if (result && result.success) {
        dispatch({
          type: ACTIONS.DELETE_NOTIFICATION,
          payload: notificationId,
        });
      } else {
        console.error(
          "Failed to delete notification:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  }, []);

  const deleteAllNotifications = useCallback(async () => {
    try {
      const result = await notificationService.deleteAllNotifications();
      if (result && result.success) {
        dispatch({ type: ACTIONS.DELETE_ALL_NOTIFICATIONS });
        previousUnreadRef.current = 0;
      } else {
        console.error(
          "Failed to delete all notifications:",
          result?.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to delete all notifications:", error);
    }
  }, []);

  const addNotification = useCallback((notification) => {
    dispatch({ type: ACTIONS.ADD_NOTIFICATION, payload: notification });
    previousUnreadRef.current = (previousUnreadRef.current ?? 0) + 1;
    playNotificationSound();
  }, []);

  const refresh = useCallback(() => {
    loadNotifications({ limit: 20, offset: 0 });
    loadStats({ detectNew: false });
  }, [loadNotifications, loadStats]);

  const value = {
    ...state,
    loadNotifications,
    loadStats,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    addNotification,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export { useNotifications } from "./useNotifications.js";
